
import React, { memo, useCallback, useState } from "react";
import { Card } from "@/components/ui/card";
import { useOptimizedCart } from "@/contexts/OptimizedCartContext";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Product } from "@/types/common";
import { handleProductNavigation } from "@/utils/productUrlUtils";
import PremiumProductBadges from "./PremiumProductBadges";
import PremiumProductActionButtons from "./PremiumProductActionButtons";
import OptimizedProductImage from "./OptimizedProductImage";
import PremiumProductInfo from "./PremiumProductInfo";

interface OptimizedPremiumProductCardProps {
  product: Product;
}

const OptimizedPremiumProductCard = memo<OptimizedPremiumProductCardProps>(({ product }) => {
  const { addToCart } = useOptimizedCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product);
    toast({
      title: "✨ Added to Luxury Collection",
      description: `${product.name} has been added to your exclusive collection.`,
    });
  }, [addToCart, product, toast]);

  const handleQuickView = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // Quick view implementation can be added here
  }, []);

  const handleProductClick = useCallback(() => {
    handleProductNavigation(product, navigate);
  }, [product, navigate]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <div 
      className="group relative transform transition-transform duration-300 hover:scale-105 hover:-translate-y-2"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Card 
        className="overflow-hidden bg-card text-card-foreground border shadow-xl hover:shadow-2xl transition-shadow duration-300 cursor-pointer relative"
        onClick={handleProductClick}
      >
        <PremiumProductBadges product={product} />
        <PremiumProductActionButtons product={product} isHovered={isHovered} />
        <OptimizedProductImage
          product={product}
          isHovered={isHovered}
          onQuickView={handleQuickView}
          onAddToCart={handleAddToCart}
        />
        <PremiumProductInfo product={product} onAddToCart={handleAddToCart} />

        {/* Optimized CSS shine effect */}
        <div
          className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-in-out pointer-events-none ${
            isHovered ? 'translate-x-full' : '-translate-x-full'
          }`}
          style={{ transform: 'skewX(-20deg)' }}
        />
      </Card>
    </div>
  );
});

OptimizedPremiumProductCard.displayName = 'OptimizedPremiumProductCard';

export default OptimizedPremiumProductCard;
